import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, FileText, ExternalLink, Save } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

const ICON_NAMES = ["Coffee", "Award", "GraduationCap", "Users", "Briefcase", "Sparkles", "Building2", "Star"];

export default function AdminAboutTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { data: about, isLoading } = useQuery({ queryKey: ["about"], queryFn: endpoints.about });
  const [editing, setEditing] = useState(null); // { key, item|"new" }
  const [heroEditing, setHeroEditing] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["about"] });
  };

  const move = async (key, items, id, dir) => {
    const ids = items.map((x) => x.id);
    const idx = ids.indexOf(id);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= ids.length) return;
    [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
    try {
      await endpoints.adminReorderAbout(key, ids);
      refresh();
    } catch {
      toast.error(isRTL ? "تعذّر الترتيب" : "Reorder failed");
    }
  };

  const remove = async (key, id) => {
    if (!window.confirm(isRTL ? "حذف هذا العنصر؟" : "Delete this item?")) return;
    try {
      await endpoints.adminDeleteAboutItem(key, id);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      refresh();
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const save = async (key, item, isNew) => {
    try {
      if (isNew) {
        await endpoints.adminAddAboutItem(key, item);
        toast.success(isRTL ? "تمت الإضافة" : "Added");
      } else {
        await endpoints.adminUpdateAboutItem(key, item.id, item);
        toast.success(isRTL ? "تم الحفظ" : "Saved");
      }
      refresh();
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    }
  };

  const saveHero = async (hero) => {
    try {
      await endpoints.adminUpdateHero(hero);
      toast.success(isRTL ? "تم الحفظ" : "Hero saved");
      refresh();
      setHeroEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    }
  };

  if (isLoading || !about) return <div className="text-[#5C544F]">…</div>;

  const timeline = about.timeline || [];
  const moments = about.moments || [];
  const certs = about.certs || [];

  return (
    <div className="space-y-10" data-testid="admin-about-tab">
      {/* Hero */}
      <Section title={isRTL ? "البطل (Hero)" : "Hero"}>
        <div className="rounded-2xl bg-white border border-[#2A2421]/10 p-4 flex gap-4 items-start">
          {about.hero?.photo && <img src={about.hero.photo} alt="" className="w-28 h-36 rounded-xl object-cover" />}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#2A2421]">{about.hero?.title_en}</div>
            <div className="text-xs text-[#5C544F]" dir="rtl">{about.hero?.title_ar}</div>
            <div className="text-xs text-[#5C544F] mt-2 line-clamp-2">{about.hero?.subtitle_en}</div>
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setHeroEditing(true)} data-testid="admin-hero-edit">
            <Pencil className="w-3.5 h-3.5 me-1" /> {isRTL ? "تعديل" : "Edit"}
          </Button>
        </div>
        {heroEditing && (
          <HeroDialog hero={about.hero} onClose={() => setHeroEditing(false)} onSave={saveHero} />
        )}
      </Section>

      {/* Timeline */}
      <Section
        title={isRTL ? `الجدول الزمني (${timeline.length})` : `Timeline (${timeline.length})`}
        action={
          <Button size="sm" className="bg-[#C86240] text-white rounded-full" onClick={() => setEditing({ key: "timeline", item: "new" })} data-testid="admin-add-timeline">
            <Plus className="w-3.5 h-3.5 me-1" /> {isRTL ? "إضافة" : "Add"}
          </Button>
        }
      >
        <div className="space-y-2">
          {timeline.map((it, i) => (
            <Row
              key={it.id}
              i={i}
              total={timeline.length}
              onUp={() => move("timeline", timeline, it.id, -1)}
              onDown={() => move("timeline", timeline, it.id, 1)}
              onEdit={() => setEditing({ key: "timeline", item: it })}
              onDelete={() => remove("timeline", it.id)}
              testIdBase={`admin-timeline-${it.id}`}
            >
              <div className="text-xs uppercase text-[#C86240] font-semibold">{it.year}</div>
              <div className="font-medium text-[#2A2421] text-sm">{it.title_en}</div>
              <div className="text-xs text-[#5C544F]" dir="rtl">{it.title_ar}</div>
            </Row>
          ))}
        </div>
      </Section>

      {/* Moments */}
      <Section
        title={isRTL ? `لحظات من الرحلة (${moments.length})` : `Moments from the Journey (${moments.length})`}
        action={
          <Button size="sm" className="bg-[#C86240] text-white rounded-full" onClick={() => setEditing({ key: "moments", item: "new" })} data-testid="admin-add-moment">
            <Plus className="w-3.5 h-3.5 me-1" /> {isRTL ? "صورة جديدة" : "New photo"}
          </Button>
        }
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {moments.map((it, i) => (
            <div key={it.id} className="rounded-xl bg-white border border-[#2A2421]/10 overflow-hidden" data-testid={`admin-moment-${it.id}`}>
              <img src={it.src} alt="" className="w-full h-32 object-cover" />
              <div className="p-3">
                <div className="text-xs text-[#2A2421]">{it.caption_en}</div>
                <div className="text-xs text-[#5C544F] mt-1" dir="rtl">{it.caption_ar}</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex gap-1">
                    <button disabled={i === 0} onClick={() => move("moments", moments, it.id, -1)} className="text-[#5C544F] hover:text-[#C86240] disabled:opacity-30" aria-label="up" data-testid={`admin-moment-up-${it.id}`}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button disabled={i === moments.length - 1} onClick={() => move("moments", moments, it.id, 1)} className="text-[#5C544F] hover:text-[#C86240] disabled:opacity-30" aria-label="down" data-testid={`admin-moment-down-${it.id}`}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="rounded-full h-7" onClick={() => setEditing({ key: "moments", item: it })} data-testid={`admin-edit-moment-${it.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 h-7" onClick={() => remove("moments", it.id)} data-testid={`admin-delete-moment-${it.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Certs */}
      <Section
        title={isRTL ? `الشهادات (${certs.length})` : `Certifications (${certs.length})`}
        action={
          <Button size="sm" className="bg-[#C86240] text-white rounded-full" onClick={() => setEditing({ key: "certs", item: "new" })} data-testid="admin-add-cert">
            <Plus className="w-3.5 h-3.5 me-1" /> {isRTL ? "شهادة" : "Add cert"}
          </Button>
        }
      >
        <div className="space-y-2">
          {certs.map((it, i) => (
            <Row
              key={it.id}
              i={i}
              total={certs.length}
              onUp={() => move("certs", certs, it.id, -1)}
              onDown={() => move("certs", certs, it.id, 1)}
              onEdit={() => setEditing({ key: "certs", item: it })}
              onDelete={() => remove("certs", it.id)}
              testIdBase={`admin-cert-${it.id}`}
            >
              <div className="font-medium text-[#2A2421] text-sm">{it.title_en}</div>
              <div className="text-xs text-[#5C544F]" dir="rtl">{it.title_ar}</div>
              {it.pdf && (
                <a href={it.pdf} target="_blank" rel="noopener noreferrer" className="text-xs text-[#C86240] inline-flex items-center gap-1 mt-1">
                  <FileText className="w-3 h-3" /> PDF <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </Row>
          ))}
        </div>
      </Section>

      {editing && (
        <ItemDialog
          key={editing.item === "new" ? "new" : editing.item.id}
          editing={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

const Section = ({ title, action, children }) => (
  <section>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-lg font-semibold text-[#2A2421]">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

const Row = ({ i, total, onUp, onDown, onEdit, onDelete, children, testIdBase }) => (
  <div className="flex items-start gap-3 bg-white rounded-xl border border-[#2A2421]/10 p-3" data-testid={testIdBase}>
    <div className="flex flex-col gap-0.5">
      <button disabled={i === 0} onClick={onUp} className="text-[#5C544F] hover:text-[#C86240] disabled:opacity-30" aria-label="up" data-testid={`${testIdBase}-up`}>
        <ArrowUp className="w-4 h-4" />
      </button>
      <button disabled={i === total - 1} onClick={onDown} className="text-[#5C544F] hover:text-[#C86240] disabled:opacity-30" aria-label="down" data-testid={`${testIdBase}-down`}>
        <ArrowDown className="w-4 h-4" />
      </button>
    </div>
    <div className="flex-1 min-w-0">{children}</div>
    <div className="flex gap-1 flex-shrink-0">
      <Button size="sm" variant="outline" className="rounded-full" onClick={onEdit} data-testid={`${testIdBase}-edit`}>
        <Pencil className="w-3.5 h-3.5" />
      </Button>
      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={onDelete} data-testid={`${testIdBase}-delete`}>
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  </div>
);

function ItemDialog({ editing, onClose, onSave }) {
  const { isRTL } = useI18n();
  const { key, item } = editing;
  const isNew = item === "new";
  const [form, setForm] = useState(isNew ? {} : { ...item });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#F9F6F0]" data-testid="admin-about-item-form">
        <DialogHeader>
          <DialogTitle className="text-start">
            {isNew ? (isRTL ? "إضافة" : "Add") : (isRTL ? "تعديل" : "Edit")} — {key}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pe-2">
          {key === "timeline" && (
            <>
              <FF label="Year"><Input value={form.year || ""} onChange={(e) => set("year", e.target.value)} placeholder="2025" /></FF>
              <FF label="Icon">
                <select className="w-full border border-[#2A2421]/15 rounded-md h-10 px-3 bg-white" value={form.icon || ""} onChange={(e) => set("icon", e.target.value)}>
                  <option value="">—</option>
                  {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </FF>
              <FF label="Title EN"><Input value={form.title_en || ""} onChange={(e) => set("title_en", e.target.value)} /></FF>
              <FF label="Title AR"><Input value={form.title_ar || ""} onChange={(e) => set("title_ar", e.target.value)} dir="rtl" /></FF>
              <FF label="Description EN"><Textarea rows={3} value={form.desc_en || ""} onChange={(e) => set("desc_en", e.target.value)} /></FF>
              <FF label="Description AR"><Textarea rows={3} value={form.desc_ar || ""} onChange={(e) => set("desc_ar", e.target.value)} dir="rtl" /></FF>
            </>
          )}
          {key === "moments" && (
            <>
              <FF label="Image URL"><Input value={form.src || ""} onChange={(e) => set("src", e.target.value)} placeholder="https://..." /></FF>
              {form.src && <img src={form.src} alt="" className="max-h-40 rounded-lg" />}
              <FF label="Caption EN"><Input value={form.caption_en || ""} onChange={(e) => set("caption_en", e.target.value)} /></FF>
              <FF label="Caption AR"><Input value={form.caption_ar || ""} onChange={(e) => set("caption_ar", e.target.value)} dir="rtl" /></FF>
            </>
          )}
          {key === "certs" && (
            <>
              <FF label="Title EN"><Input value={form.title_en || ""} onChange={(e) => set("title_en", e.target.value)} /></FF>
              <FF label="Title AR"><Input value={form.title_ar || ""} onChange={(e) => set("title_ar", e.target.value)} dir="rtl" /></FF>
              <FF label="PDF URL (optional)"><Input value={form.pdf || ""} onChange={(e) => set("pdf", e.target.value)} placeholder="https://...pdf" /></FF>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#2A2421]/10">
          <Button variant="ghost" onClick={onClose}>{isRTL ? "إلغاء" : "Cancel"}</Button>
          <Button onClick={() => onSave(key, form, isNew)} className="bg-[#2A2421] text-white" data-testid="admin-save-about-item">
            <Save className="w-3.5 h-3.5 me-1" /> {isRTL ? "حفظ" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HeroDialog({ hero, onClose, onSave }) {
  const { isRTL } = useI18n();
  const [form, setForm] = useState({ ...hero });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#F9F6F0]" data-testid="admin-hero-form">
        <DialogHeader>
          <DialogTitle className="text-start">{isRTL ? "تعديل البطل (Hero)" : "Edit Hero"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FF label="Title EN"><Input value={form.title_en || ""} onChange={(e) => set("title_en", e.target.value)} /></FF>
          <FF label="Title AR"><Input value={form.title_ar || ""} onChange={(e) => set("title_ar", e.target.value)} dir="rtl" /></FF>
          <FF label="Subtitle EN"><Textarea rows={3} value={form.subtitle_en || ""} onChange={(e) => set("subtitle_en", e.target.value)} /></FF>
          <FF label="Subtitle AR"><Textarea rows={3} value={form.subtitle_ar || ""} onChange={(e) => set("subtitle_ar", e.target.value)} dir="rtl" /></FF>
          <FF label="Main Photo URL"><Input value={form.photo || ""} onChange={(e) => set("photo", e.target.value)} /></FF>
          <FF label="Secondary Photo URL"><Input value={form.photo_secondary || ""} onChange={(e) => set("photo_secondary", e.target.value)} /></FF>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#2A2421]/10">
          <Button variant="ghost" onClick={onClose}>{isRTL ? "إلغاء" : "Cancel"}</Button>
          <Button onClick={() => onSave(form)} className="bg-[#2A2421] text-white" data-testid="admin-save-hero">
            <Save className="w-3.5 h-3.5 me-1" /> {isRTL ? "حفظ" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FF = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs text-[#5C544F] mb-1">{label}</span>
    {children}
  </label>
);
